import React, { useEffect, useMemo, useState } from 'react'
import SlideBase from './SlideBase'
import WrappedVideoBackground from '../WrappedVideoBackground'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import useDistanceDisplay from 'hooks/useDistanceDisplay'
import { AVERAGE_TRAVELLED_DISTANCE_KM } from '../../config/averages'

// Get video config for this slide (static, doesn't depend on runtime data)
const SLIDE_VIDEO = WRAPPED_SLIDES.find(s => s.id === 'distance')?.video
const SLIDE_ID = 'distance' as const

function getHeading(hasTravelled: boolean, isFarTraveller: boolean): string {
  if (!hasTravelled) return 'On average, our travellers journeyed far and wide with us'
  if (isFarTraveller) return 'You journeyed far and wide with us'
  return 'You proved the best escapes are closest to home'
}

function DistanceSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)
  const [animate, setAnimate] = useState(!SLIDE_VIDEO) // If no video, animate immediately

  const hasTravelled = !!(data?.stats.citiesVisited && data.stats.totalDistanceKm > 0)
  const travelledDistanceKm = hasTravelled ? data.stats.totalDistanceKm : AVERAGE_TRAVELLED_DISTANCE_KM
  const { displayDistance, unitLabel } = useDistanceDisplay(travelledDistanceKm)

  // Start animation when video is ready (only when this is the current slide)
  useEffect(() => {
    if (SLIDE_VIDEO && isVideoReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isVideoReady, isCurrentSlide])

  // Memoize background to prevent recreation
  const videoBackground = useMemo(() => {
    if (!SLIDE_VIDEO) return undefined
    return <WrappedVideoBackground video={SLIDE_VIDEO} isActive={isCurrentSlide} />
  }, [isCurrentSlide])

  if (!data) return null

  const { stats } = data
  const isFarTraveller = stats.totalDistanceKm > 200
  const heading = getHeading(hasTravelled, isFarTraveller)

  return (
    <SlideBase background={videoBackground}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">Total Distance Travelled</Caption>
            <Heading variant="heading5"><b>{heading}</b></Heading>
          </Group>
          <Heading variant="heading1"><i><FormatDecimal value={displayDistance} maximumFractionDigits={0} /> {unitLabel}</i></Heading>
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(DistanceSlide)
